/**
 * Расшифровка как документ для суда: цитата с отметкой времени и файл Word.
 *
 * Зачем. Расшифровку заседания (или записи разговора, которую несут в суд как
 * доказательство) юрист не читает — он на неё ССЫЛАЕТСЯ: «на 00:23:45
 * аудиозаписи истец заявил…», прикладывает её к замечаниям на протокол или к
 * жалобе. Обычная выгрузка «имя файла + абзацы» для этого не годится: в ней нет
 * ни дела, ни даты заседания, ни сведений о записи, ни места для подписи, а
 * время стоит в виде «75:03».
 *
 * На 07.10.2026 такого документа не отдавал ни один русский сервис расшифровки:
 * ближайший пишет «стенограмму… по образцу вы оформляете сами». Форма собрана
 * по тому, что требует закон и что советуют практики (источники и цитаты —
 * docs/court-transcript.md):
 *   · у записи-доказательства сказано, «когда, кем и в каких условиях
 *     осуществлялись записи» (ч. 1 ст. 77 ГПК РФ);
 *   · время — от начала записи, чч:мм:сс, у каждой реплики: суд слушать запись
 *     не станет, пока ему не назвали место;
 *   · текст называется «расшифровка аудиозаписи»: «стенографической записью»
 *     кодексы зовут запись самого суда.
 *
 * Модуль не знает страницы: сегменты и ответы формы на входе, список блоков на
 * выходе; из блоков делаются предпросмотр и файл. Все слова приходят словарём T
 * (resources/lang/ru/tools/…): форма русская, как и процесс, для которого она.
 */
(function (root) {
    'use strict';

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    /** Отметка времени для документа: всегда чч:мм:сс. */
    function clock(sec) {
        sec = Math.max(0, Math.round(sec || 0));
        return pad(Math.floor(sec / 3600)) + ':' + pad(Math.floor((sec % 3600) / 60)) + ':' + pad(sec % 60);
    }

    /** 2026-09-28 → 28.09.2026; пустое или непонятное → ''. */
    function ruDate(iso) {
        var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
        return m ? m[3] + '.' + m[2] + '.' + m[1] : '';
    }

    /**
     * Дата и время заседания из имени файла, которым запись называет сам суд:
     * «год.месяц.число__часы.минуты» (суды общей юрисдикции, Регламент Судебного
     * департамента № 2 от 09.01.2025, п. 4.4) и «ГОД-МЕСЯЦ-ЧИСЛО_ЧАС-МИН»
     * (арбитражные суды, Инструкция по делопроизводству, п. 6.6).
     * @returns {{date:string, time:string}|null} date — ГГГГ-ММ-ДД
     */
    function fileMoment(name) {
        var m = /(?:^|[^0-9])(20\d{2})[.\-_](\d{2})[.\-_](\d{2})[_\s\-]{1,3}(\d{2})[.\-_:](\d{2})(?![0-9])/.exec(String(name || ''));
        if (!m) return null;
        var mo = +m[2], d = +m[3], h = +m[4], mi = +m[5];
        if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
        return { date: m[1] + '-' + m[2] + '-' + m[3], time: m[4] + ':' + m[5] };
    }

    function fill(tpl, vars) {
        return String(tpl || '').replace(/:([a-z_]+)/g, function (all, k) {
            return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : all;
        });
    }

    function trim(v) { return String(v == null ? '' : v).replace(/\s+/g, ' ').trim(); }

    function upperFirst(v) { v = trim(v); return v ? v.charAt(0).toUpperCase() + v.slice(1) : ''; }

    /**
     * Адресат в шапке. Суд вписывают в именительном падеже, и «В Ленинский
     * районный суд» из него получается само. У мирового судьи — нет: шапка
     * «В Мировой судья…» неграмотна, а пишут ему в дательном и без предлога.
     */
    function courtLine(court, T) {
        var m = /^миров(?:ой|ому)\s+судь[яе]\s*(.*)$/i.exec(court);
        if (m) return (T.head_magistrate || 'Мировому судье') + (m[1] ? ' ' + m[1] : '');
        return fill(T.head_court, { court: court });
    }

    /**
     * Цитата реплики для документа: «На 00:23:45 аудиозаписи истец: «…»».
     * Имя говорящего ставится как есть; без метки — безлично.
     */
    function quote(para, T) {
        var text = trim((para.segs || []).map(function (s) { return s.text; }).join(' '));
        if (!text) return '';
        var who = trim(para.speaker);
        return fill(who ? T.quote_named : T.quote_plain, { time: clock(para.start), who: who, text: text });
    }

    /**
     * @param {Array} paras абзацы расшифровки ({speaker, start, segs[]})
     * @param {Object} f ответы формы:
     *   kind 'hearing'|'talk'; court, caseNo, date (ГГГГ-ММ-ДД), time (чч:мм);
     *   source 'court'|'own'; recorder; when, conditions (для разговора);
     *   author, authorRole; fileName, durationSec; speakers (bool)
     * @param {Object} T словарь
     * @returns {Array} блоки: {k:'head'|'title'|'sub'|'p'|'note'|'turn'|'gap'|'sign', …}
     */
    function build(paras, f, T) {
        f = f || {};
        var hearing = f.kind !== 'talk';
        var blank = T.blank || '____________';
        var court = trim(f.court), caseNo = trim(f.caseNo), author = trim(f.author), role = trim(f.authorRole);
        var out = [];

        /* Шапка — только когда есть кому адресовать: пустые «В ____» наверху
           расшифровки, которую человек делает для себя, были бы мусором. */
        var head = [];
        if (court) head.push(courtLine(court, T));
        if (caseNo) head.push(fill(T.head_case, { case: caseNo }));
        /* «Истец: Орлова Е. В.» — так называют себя в шапке процессуальных
           документов, и фамилию не приходится склонять («от Орлова…» вместо
           «от Орловой…» выходило при первой же проверке). */
        if (author) head.push(role ? fill(T.head_party, { role: upperFirst(role), who: author }) : author);
        if (head.length && (court || caseNo)) out.push({ k: 'head', lines: head });

        var date = ruDate(f.date), time = /^\d{2}:\d{2}$/.test(trim(f.time)) ? trim(f.time) : '';
        if (hearing) {
            out.push({ k: 'title', text: T.title_hearing });
            out.push({ k: 'sub', text: fill(caseNo ? T.sub_hearing : T.sub_hearing_nocase,
                { date: date ? fill(T.date_of, { date: date }) : T.date_blank, case: caseNo }) });
        } else {
            out.push({ k: 'title', text: T.title_talk });
            out.push({ k: 'sub', text: T.sub_talk });
        }
        out.push({ k: 'gap' });

        if (hearing) {
            if (court) out.push({ k: 'p', text: fill(T.line_court, { court: court }) });
            if (date) out.push({ k: 'p', text: fill(time ? T.line_when_time : T.line_when, { date: date, time: time }) });
            out.push({ k: 'p', text: f.source === 'own'
                ? fill(T.line_source_own, { who: trim(f.recorder) || blank })
                : T.line_source_court });
        } else {
            /* Три обязательных сведения о записи-доказательстве: когда, кем и в
               каких условиях. Пропущенное остаётся пропуском в файле — его
               допишут ручкой, но не забудут. */
            out.push({ k: 'p', text: fill(T.line_talk_when, { v: trim(f.when) || blank }) });
            out.push({ k: 'p', text: fill(T.line_talk_who, { v: trim(f.recorder) || blank }) });
            out.push({ k: 'p', text: fill(T.line_talk_how, { v: trim(f.conditions) || blank }) });
        }
        var fileName = trim(f.fileName), dur = Math.round(f.durationSec || 0);
        if (fileName || dur) {
            out.push({ k: 'p', text: fileName && dur ? fill(T.line_file_len, { name: fileName, len: clock(dur) })
                : fileName ? fill(T.line_file, { name: fileName }) : fill(T.line_len, { len: clock(dur) }) });
        }

        var names = [], seen = {};
        (paras || []).forEach(function (p) { var n = trim(p.speaker); if (n && !seen[n]) { seen[n] = 1; names.push(n); } });
        if (f.speakers !== false && names.length) out.push({ k: 'p', text: fill(T.line_people, { list: names.join(', ') }) });
        out.push({ k: 'note', text: T.line_clock });
        out.push({ k: 'gap' });

        var prev = null, turns = 0;
        (paras || []).forEach(function (p) {
            var text = trim((p.segs || []).map(function (s) { return s.text; }).join(' '));
            if (!text) return;
            var who = f.speakers !== false ? trim(p.speaker) : '';
            out.push({ k: 'turn', time: clock(p.start), who: who && who !== prev ? who : '', text: text });
            if (who) prev = who;
            turns++;
        });

        /* Подпись не должна уехать на пустую страницу одна: последняя реплика
           и пробел перед подписью держатся за неё. */
        for (var q = out.length - 1; q >= 0; q--) { if (out[q].k === 'turn') { out[q].keep = true; break; } }
        out.push({ k: 'gap', keep: true });
        out.push({ k: 'sign', text: fill(T.sign, { who: author || blank }), keep: true });
        out.push({ k: 'sign', text: T.sign_date });
        return { blocks: out, turns: turns, hearing: hearing };
    }

    /** Блоки → параметры DocxWriter (режим blocks). */
    function toDocx(blocks) {
        var HEAD = 4962, out = [];
        blocks.forEach(function (b) {
            if (b.k === 'head') b.lines.forEach(function (l) { out.push({ text: l, indentLeft: HEAD }); });
            else if (b.k === 'title') out.push({ text: b.text, align: 'center', bold: true, keepNext: true, spaceBefore: 240 });
            else if (b.k === 'sub') out.push({ text: b.text, align: 'center', keepNext: true });
            else if (b.k === 'gap') out.push({ text: '', keepNext: !!b.keep });
            else if (b.k === 'note') out.push({ text: b.text, italic: true, size: 22 });
            else if (b.k === 'sign') out.push({ text: b.text, spaceBefore: 120, keepNext: !!b.keep });
            else if (b.k === 'turn') {
                var runs = [{ text: '[' + b.time + '] ', color: '595959' }];
                if (b.who) runs.push({ text: b.who + ': ', bold: true });
                runs.push({ text: b.text });
                out.push({ runs: runs, align: 'both', spaceAfter: 120, keepNext: !!b.keep });
            } else out.push({ text: b.text, align: 'both' });
        });
        return out;
    }

    /** Блоки → простой текст (буфер обмена, проверка глазами). */
    function toPlain(blocks) {
        var out = [];
        blocks.forEach(function (b) {
            if (b.k === 'head') out.push(b.lines.join('\n'), '');
            else if (b.k === 'gap') out.push('');
            else if (b.k === 'turn') out.push('[' + b.time + '] ' + (b.who ? b.who + ': ' : '') + b.text, '');
            else out.push(b.text);
        });
        return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    }

    root.__transcriptCourt = {
        clock: clock, ruDate: ruDate, fileMoment: fileMoment, quote: quote,
        build: build, toDocx: toDocx, toPlain: toPlain,
    };
})(typeof window !== 'undefined' ? window : globalThis);
